import {
  Button,
  Callout,
  Input,
  Panel,
  Select,
  Switch,
  Text,
  Textarea,
  toast,
} from "@imagedesignai/neo";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  GENERATION_CONFIG_NAME_MAX_LENGTH,
  normalizeGenerationConfigName,
} from "../../src/generation-configs.ts";
import {
  GENERATION_SETTINGS_CONTRACT_VERSION,
  type GenerationSettings,
  type GenerationSettingsField,
  normalizeGenerationSettings,
} from "../../src/generation-settings.ts";
import {
  type GenerationConfigSurface,
  type GenerationConfigView,
  type GifmeApiClient,
  gifmeErrorMessage,
} from "./api-client.ts";
import { ModelRegistryPanel } from "./model-registry-panel.tsx";

export interface AdminViewProps {
  api: GifmeApiClient;
}

type GenerationSettingsDraft = {
  [Name in keyof GenerationSettings]: GenerationSettings[Name] | "";
};

type GenerationSettingsDraftValue = GenerationSettingsDraft[keyof GenerationSettings];

function SettingsField({
  field,
  value,
  onChange,
}: {
  field: GenerationSettingsField;
  value: GenerationSettingsDraftValue;
  onChange(value: GenerationSettingsDraftValue): void;
}) {
  if (field.control === "boolean") {
    return (
      <Switch
        label={field.label}
        description={field.description}
        checked={Boolean(value)}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
    );
  }
  if (field.control === "select") {
    return (
      <Select
        label={field.label}
        options={field.options.map((option) => ({ value: option, label: option }))}
        value={String(value)}
        onChange={onChange}
      />
    );
  }
  if (field.control === "number") {
    return (
      <Input
        label={field.label}
        description={field.description}
        type="number"
        min={field.min}
        max={field.max}
        step={field.step}
        value={String(value)}
        onChange={(event) => {
          const next = event.currentTarget.value;
          onChange(next === "" ? "" : Number(next));
        }}
      />
    );
  }
  if (field.multiline) {
    return (
      <Textarea
        label={field.label}
        description={field.description}
        maxLength={field.maxLength}
        value={String(value)}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    );
  }
  return (
    <Input
      label={field.label}
      description={field.description}
      maxLength={field.maxLength}
      value={String(value)}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  );
}

function replaceConfig(
  configs: readonly GenerationConfigView[],
  replacement: GenerationConfigView,
): GenerationConfigView[] {
  const exists = configs.some(({ id }) => id === replacement.id);
  const next = exists
    ? configs.map((config) => (config.id === replacement.id ? replacement : config))
    : [...configs, replacement];
  return [...next].sort((left, right) => left.name.localeCompare(right.name));
}

function GenerationConfigPanel({ api }: AdminViewProps) {
  const [surface, setSurface] = useState<GenerationConfigSurface | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState("");
  const [settings, setSettings] = useState<GenerationSettingsDraft | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingDefault, setIsChangingDefault] = useState(false);

  const selectConfig = useCallback((config: GenerationConfigView) => {
    setSelectedId(config.id);
    setIsCreating(false);
    setName(config.name);
    setSettings(config.settings);
    setActionError(null);
  }, []);

  const loadSettings = useCallback(
    async (preferredId?: string): Promise<boolean> => {
      setLoadError(null);
      try {
        const payload = await api.json<GenerationConfigSurface>("/api/generation-configs");
        if (payload.contractVersion !== GENERATION_SETTINGS_CONTRACT_VERSION) {
          throw new Error(`Unsupported generation settings contract ${payload.contractVersion}`);
        }
        const initial =
          (preferredId ? payload.configs.find(({ id }) => id === preferredId) : undefined) ??
          payload.configs.find(({ id }) => id === payload.registry.defaultConfigId) ??
          payload.configs[0];
        if (!initial) throw new Error("No generation configurations are available");
        setSurface(payload);
        selectConfig(initial);
        return true;
      } catch (error) {
        setSurface(null);
        setSettings(null);
        setLoadError(gifmeErrorMessage(error, "Could not load output configurations."));
        return false;
      }
    },
    [api, selectConfig],
  );

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const selected = useMemo(
    () => surface?.configs.find(({ id }) => id === selectedId) ?? null,
    [selectedId, surface],
  );
  const validation = useMemo(() => {
    if (!settings) return { name: null, settings: null, error: null };
    try {
      return {
        name: normalizeGenerationConfigName(name),
        settings: normalizeGenerationSettings(settings),
        error: null,
      };
    } catch (error) {
      return {
        name: null,
        settings: null,
        error: error instanceof Error ? error.message : "Invalid generation configuration",
      };
    }
  }, [name, settings]);

  const beginNew = () => {
    if (!settings) return;
    setSelectedId(null);
    setIsCreating(true);
    setName("");
    setSettings({ ...settings });
    setActionError(null);
  };

  const save = async () => {
    if (!surface || !validation.name || !validation.settings) return;
    if (!isCreating && !selected) return;
    setIsSaving(true);
    setActionError(null);
    try {
      const path = isCreating
        ? "/api/generation-configs"
        : `/api/generation-configs/${selected?.id}`;
      const payload = await api.json<{ config: GenerationConfigView }>(path, {
        method: isCreating ? "POST" : "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: validation.name,
          settings: validation.settings,
          ...(!isCreating && selected ? { expectedRevision: selected.revision } : {}),
        }),
      });
      setSurface((current) =>
        current ? { ...current, configs: replaceConfig(current.configs, payload.config) } : current,
      );
      selectConfig(payload.config);
      toast.positive(isCreating ? "Configuration created" : "Configuration saved");
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (message === "generation_config_revision_conflict" && selected) {
        const didReload = await loadSettings(selected.id);
        if (didReload) {
          setActionError("This configuration changed elsewhere. The latest revision is loaded.");
        }
      } else {
        setActionError(gifmeErrorMessage(error, "The configuration could not be saved."));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const makeDefault = async () => {
    if (!surface || !selected || selected.id === surface.registry.defaultConfigId) return;
    setIsChangingDefault(true);
    setActionError(null);
    try {
      const payload = await api.json<{ registry: GenerationConfigSurface["registry"] }>(
        "/api/generation-configs/default",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            configId: selected.id,
            expectedRevision: surface.registry.revision,
          }),
        },
      );
      setSurface((current) => (current ? { ...current, registry: payload.registry } : current));
      toast.positive("Default configuration changed");
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (message === "generation_config_registry_revision_conflict") {
        const didReload = await loadSettings(selected.id);
        if (didReload) {
          setActionError("The default changed elsewhere. The latest registry state is loaded.");
        }
      } else {
        setActionError(gifmeErrorMessage(error, "The default configuration could not be changed."));
      }
    } finally {
      setIsChangingDefault(false);
    }
  };

  if (loadError) {
    return (
      <Panel title="Output settings" icon="settings" className="generation-config-panel">
        <div className="admin-settings-form">
          <Callout intent="danger" label="Could not load output configurations">
            {loadError}
          </Callout>
          <Button type="button" intent="secondary" onClick={() => void loadSettings()}>
            Retry settings
          </Button>
        </div>
      </Panel>
    );
  }
  if (!surface || !settings) {
    return (
      <Panel title="Output settings" icon="settings" className="generation-config-panel">
        <Text kind="body" tone="muted">
          Loading configurations…
        </Text>
      </Panel>
    );
  }

  return (
    <Panel title="Output settings" icon="settings" className="generation-config-panel">
      <div className="admin-settings-form">
        <div className="generation-config-toolbar">
          <Select
            label="Configuration"
            options={surface.configs.map((config) => ({
              value: config.id,
              label:
                config.id === surface.registry.defaultConfigId
                  ? `${config.name} (default)`
                  : config.name,
            }))}
            value={selectedId ?? ""}
            disabled={isSaving || isChangingDefault}
            onChange={(id) => {
              const config = surface.configs.find((candidate) => candidate.id === id);
              if (config) selectConfig(config);
            }}
          />
          <Button
            type="button"
            intent="secondary"
            disabled={isSaving || isChangingDefault}
            onClick={beginNew}
          >
            New configuration
          </Button>
        </div>
        <Input
          label="Configuration name"
          maxLength={GENERATION_CONFIG_NAME_MAX_LENGTH}
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
        />
        <div className="admin-settings-grid">
          {surface.fields.map((field) => (
            <SettingsField
              key={field.name}
              field={field}
              value={settings[field.name]}
              onChange={(value) =>
                setSettings((current) => (current ? { ...current, [field.name]: value } : current))
              }
            />
          ))}
        </div>
        {validation.error ? (
          <Callout intent="danger" label="Configuration is not valid">
            {validation.error}
          </Callout>
        ) : null}
        {actionError ? (
          <Callout intent="danger" label="Configuration was not saved">
            {actionError}
          </Callout>
        ) : null}
        <div className="generation-config-actions">
          <Button
            type="button"
            intent="primary"
            loading={isSaving}
            disabled={!validation.name || !validation.settings || isChangingDefault}
            onClick={() => void save()}
          >
            Save configuration
          </Button>
          {!isCreating && selected ? (
            <Button
              type="button"
              intent="secondary"
              loading={isChangingDefault}
              disabled={selected.id === surface.registry.defaultConfigId || isSaving}
              onClick={() => void makeDefault()}
            >
              {selected.id === surface.registry.defaultConfigId
                ? "Current default"
                : "Make default"}
            </Button>
          ) : null}
          <Text kind="caption" tone="muted">
            {isCreating
              ? `New configuration · contract ${surface.contractVersion}`
              : `Revision ${selected?.revision ?? "—"} · contract ${surface.contractVersion}`}
          </Text>
        </div>
      </div>
    </Panel>
  );
}

export function AdminView({ api }: AdminViewProps) {
  return (
    <div className="admin-control-grid">
      <ModelRegistryPanel api={api} />
      <GenerationConfigPanel api={api} />
    </div>
  );
}
